import {memo, useMemo, type ReactNode} from 'react'
import {clsx} from 'clsx'

import {getBackgroundColor, getLineNumberWidth} from '@github-ui/diffs/diff-line-helpers'
import {SplitDiffTable} from '@github-ui/diffs/DiffParts'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLDiv} from '@github-ui/safe-html/VerifiedHTML'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import type {StaticDiffLine} from '@github-ui/diff-lines/conversation-types'
import styles from './StaticSplitDiffPreview.module.css'

type PairedLine = {left: StaticDiffLine | null; right: StaticDiffLine | null}

/**
 * Pairs a flat `StaticDiffLine[]` into left/right columns for side-by-side rendering, mirroring the pairing logic in
 * `SimpleSplitDiffLines`' `groupDiffLines()` (`@github-ui/diff-lines`'s `components/simple-diff`) but operating on the
 * static, store-free line shape.
 */
function pairStaticDiffLines(lines: StaticDiffLine[]): PairedLine[] {
  const leftLines: Array<StaticDiffLine | null> = []
  const rightLines: Array<StaticDiffLine | null> = []

  const fillBlanks = () => {
    while (leftLines.length < rightLines.length) leftLines.push(null)
    while (rightLines.length < leftLines.length) rightLines.push(null)
  }

  for (const line of lines) {
    switch (line.type) {
      case 'ADDITION':
        rightLines.push(line)
        break
      case 'DELETION':
        leftLines.push(line)
        break
      case 'CONTEXT':
      case 'INJECTED_CONTEXT':
      case 'HUNK':
        fillBlanks()
        leftLines.push(line)
        rightLines.push(line)
        break
      default:
        break
    }
  }
  fillBlanks()

  const length = Math.max(leftLines.length, rightLines.length)
  const paired: PairedLine[] = []
  for (let i = 0; i < length; i++) {
    paired.push({left: leftLines[i] ?? null, right: rightLines[i] ?? null})
  }
  return paired
}

function HunkCell({line}: {line: StaticDiffLine}) {
  return (
    <td colSpan={4} className={styles.diffHunkCell} valign="top">
      <code className={styles.diffHunkText}>
        {/* Explicitly mark html as safe because it is server-sanitized or escaped at parse time */}
        <VerifiedHTMLDiv
          className={clsx(styles.diffTextInner, 'color-fg-muted')}
          /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side or escaped by parseUnifiedDiff. */
          html={line.html as SafeHTMLString}
        />
      </code>
    </td>
  )
}

function LineNumberCell({line, children}: {line: StaticDiffLine | null; children: ReactNode}) {
  return (
    <td
      className={styles.diffLineNumber}
      style={{backgroundColor: line ? getBackgroundColor(line.type, true) : undefined}}
    >
      <code>{children}</code>
    </td>
  )
}

function SideCell({line, marker}: {line: StaticDiffLine | null; marker: '+' | '-' | undefined}) {
  if (!line) return <td className={styles.diffTextCell} />

  const showLineMarker = !!marker

  return (
    <td className={styles.diffTextCell} style={{backgroundColor: getBackgroundColor(line.type, false)}}>
      <code
        className={clsx(
          line.type === 'ADDITION' && styles.syntaxHighlightedAdditionLine,
          line.type === 'DELETION' && styles.syntaxHighlightedDeletionLine,
        )}
      >
        {showLineMarker && <span className={styles.diffTextMarker}>{marker}</span>}
        {/* Explicitly mark html as safe because it is server-sanitized or escaped at parse time */}
        {/* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- Composed from sanitized/escaped segments */}
        <VerifiedHTMLDiv className={styles.diffTextInner} html={line.html as SafeHTMLString} />
      </code>
    </td>
  )
}

function SplitDiffRow({pair}: {pair: PairedLine}) {
  const isHunkRow = pair.left?.type === 'HUNK' || pair.right?.type === 'HUNK'
  if (isHunkRow) {
    const hunkLine = pair.left?.type === 'HUNK' ? pair.left : pair.right
    if (!hunkLine) return null
    return (
      <tr>
        <HunkCell line={hunkLine} />
      </tr>
    )
  }

  return (
    <tr>
      <LineNumberCell line={pair.left}>{pair.left?.left}</LineNumberCell>
      <SideCell line={pair.left} marker={pair.left?.type === 'DELETION' ? '-' : undefined} />
      <LineNumberCell line={pair.right}>{pair.right?.right}</LineNumberCell>
      <SideCell line={pair.right} marker={pair.right?.type === 'ADDITION' ? '+' : undefined} />
    </tr>
  )
}

export interface StaticSplitDiffPreviewProps {
  diffLines?: StaticDiffLine[]
  className?: string
}

/**
 * Renders a static, non-interactive side-by-side (split) representation of a diff — a store-free sibling to
 * `@github-ui/diff-lines`'s `StaticUnifiedDiffPreview`/`SimpleSplitDiffLines` for `DiffWorkbench`'s changeset-derived
 * preview, kept in `agent-sessions` (its only consumer) rather than `@github-ui/diff-lines` since it only *consumes*
 * that package's published APIs rather than needing to live inside it.
 */
export const StaticSplitDiffPreview = memo(function StaticSplitDiffPreviewComponent({
  diffLines,
  className,
}: StaticSplitDiffPreviewProps) {
  const pairedLines = useMemo(() => (diffLines ? pairStaticDiffLines(diffLines) : []), [diffLines])

  if (!diffLines || diffLines.length < 1) {
    return null
  }

  const lineWidth = getLineNumberWidth(diffLines)

  return (
    <div className={clsx(styles.StaticSplitDiffPreview, className)} onCopy={e => e.stopPropagation()}>
      <table className={styles.splitDiffLines}>
        <SplitDiffTable lineWidth={lineWidth}>
          {pairedLines.map((pair, index) => (
            // eslint-disable-next-line @eslint-react/no-array-index-key
            <SplitDiffRow key={index} pair={pair} />
          ))}
        </SplitDiffTable>
      </table>
    </div>
  )
})
